import { LogManager } from "@mcleod/core";
import { BehaviorTypes, Button, ClickEvent, Image, Panel } from "@mcleod/components";
import { ImageType } from "@mcleod/components/src/components/image/ImageType";
import { ButtonVariant } from "@mcleod/components/src/components/button/ButtonVariant";
import { ImageName } from "@mcleod/images";
import { CapturedImage } from "./CapturedImage";
import { CapturedImageThumbnailStyles } from "./CapturedImageThumbnailStyles";
import { ImageBundleBehavior } from "./ImageBundleBehavior";

const log = LogManager.getLogger("capture.CapturedImageThumbnail");

/**
 * Props for CapturedImageThumbnail component.
 */
export interface CapturedImageThumbnailProps {
    /** The captured image data to display */
    capturedImage: CapturedImage;
    /** Size of the thumbnail in pixels (width and height) */
    size?: number;
    /** Callback when the thumbnail is tapped (for full-screen view) */
    onTap?: (capturedImage: CapturedImage) => void;
    /** Callback when the delete button is tapped */
    onDelete?: (capturedImage: CapturedImage) => void;
}

/**
 * Displays a captured image as a thumbnail with a delete button overlay.
 * Tapping the thumbnail opens full-screen view, tapping delete removes the image.
 */
export class CapturedImageThumbnail extends Panel {
    private static _behavior: ImageBundleBehavior;
    private _capturedImage: CapturedImage;
    private _thumbnailImage: Image;
    private _deleteButton: Button;
    private _onTap: (capturedImage: CapturedImage) => void;
    private _onDelete: (capturedImage: CapturedImage) => void;

    constructor(props?: Partial<CapturedImageThumbnailProps>) {
        super({}, false);

        const behaviors = this.behavior;
        const size = props?.size ?? behaviors.thumbnailSize;
        const deleteButtonSize = behaviors.thumbnailDeleteButtonSize;
        const deleteIconSize = behaviors.thumbnailDeleteIconSize;
        const borderRadius = behaviors.thumbnailBorderRadius;
        const margin = behaviors.thumbnailMargin;

        this.setProps({
            width: size,
            height: size,
            minWidth: size,
            minHeight: size,
            maxWidth: size,
            maxHeight: size,
            padding: 0,
            margin: margin,
            borderRadius: borderRadius,
            rowBreak: false
        });

        this._element.classList.add(CapturedImageThumbnailStyles.thumbnailContainer);

        this._thumbnailImage = new Image({
            imageType: ImageType.IMG,
            width: size,
            height: size,
            preserveAspectRatio: true
        });
        this._thumbnailImage._element.classList.add(CapturedImageThumbnailStyles.thumbnailImage);
        this._thumbnailImage.addClickListener((event: ClickEvent) => this.handleThumbnailTap(event));

        this._deleteButton = new Button({
            variant: ButtonVariant.round,
            imageName: ImageName.add, // The product later defines "circleDelete" as of 26.2.
            imageRotation: 45,        // For backport's sake, spin .add 45 degrees to make it an x inside a circle.
            imageColor: "white",
            backgroundColor: "waring.dark",
            imageWidth: deleteIconSize,
            imageHeight: deleteIconSize,
            width: deleteButtonSize,
            height: deleteButtonSize,
            minWidth: deleteButtonSize,
            minHeight: deleteButtonSize,
            padding: 0
        });
        this._deleteButton._element.classList.add(CapturedImageThumbnailStyles.deleteButton);
        this._deleteButton.addClickListener((event: ClickEvent) => this.handleDeleteTap(event));

        this.add(this._thumbnailImage, this._deleteButton);

        if (props?.capturedImage) {
            this.capturedImage = props.capturedImage;
        }
        if (props?.onTap) {
            this._onTap = props.onTap;
        }
        if (props?.onDelete) {
            this._onDelete = props.onDelete;
        }
    }

    /**
     * Gets the captured image data.
     */
    get capturedImage(): CapturedImage {
        return this._capturedImage;
    }

    /**
     * Sets the captured image data and updates the display.
     */
    set capturedImage(value: CapturedImage) {
        this._capturedImage = value;
        this.updateThumbnailDisplay();
    }

    /**
     * Sets the tap callback.
     */
    set onTap(callback: (capturedImage: CapturedImage) => void) {
        this._onTap = callback;
    }

    /**
     * Sets the delete callback.
     */
    set onDelete(callback: (capturedImage: CapturedImage) => void) {
        this._onDelete = callback;
    }

    /**
     * Updates the thumbnail image display based on the captured image data.
     */
    private updateThumbnailDisplay(): void {
        if (!this._capturedImage?.photo) {
            return;
        }

        const photo = this._capturedImage.photo;

        // Prefer webPath for displaying, fall back to dataUrl or base64
        if (photo.webPath) {
            this._thumbnailImage._element.setAttribute("src", photo.webPath);
        } else if (photo.dataUrl) {
            this._thumbnailImage._element.setAttribute("src", photo.dataUrl);
        } else if (photo.base64String) {
            const mimeType = photo.format === "png" ? "image/png" : "image/jpeg";
            this._thumbnailImage._element.setAttribute("src", `data:${mimeType};base64,${photo.base64String}`);
        }
    }

    /**
     * Handles tap on the thumbnail image to open full-screen view.
     */
    private handleThumbnailTap(event: ClickEvent): void {
        event.shouldAutomaticallyStopPropagation = true;
        if (this._onTap && this._capturedImage) {
            this._onTap(this._capturedImage);
        }
    }

    /**
     * Handles tap on the delete button to remove the image.
     */
    private handleDeleteTap(event: ClickEvent): void {
        event.shouldAutomaticallyStopPropagation = true;
        if (this._onDelete && this._capturedImage) {
            this._onDelete(this._capturedImage);
        }
    }

    private get behavior(): ImageBundleBehavior {
        CapturedImageThumbnail._behavior ??= BehaviorTypes.get("ImageBundle");
        return CapturedImageThumbnail._behavior;
    }
}


